import { motion } from "motion/react";
import { Brain, CheckCircle2, Network, Cloud, Activity, Zap, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

const ASYA_TAGS = ["AI-POWERED", "MICROSERVICES", "RAG ENABLED"] as const;
const AI_ITEMS = ["Custom LLM", "RAG Systems", "Industrial IoT Solutions", "Predictive Analytics"] as const;
const INFRA_ITEMS = ["Edge Ready", "IoT Gateway", "NATS High-Speed", "Ultra Secure"] as const;

export default function Solutions() {
  const { t } = useTranslation();

  return (
    <section id="solutions" className="py-24 bg-white scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-on-surface mb-4">{t('solutions.title')}</h2>
          <p className="text-on-surface-variant max-w-xl">{t('solutions.description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div
            whileHover={{ y: -5 }}
            className="md:col-span-8 bg-surface-container-low border border-outline-variant p-8 rounded-xl flex flex-col justify-between group hover:border-primary-container transition-all"
          >
            <div>
              <div className="w-12 h-12 rounded flex items-center justify-center text-white mb-6">
                <img src="/client/logo-asya.png" alt="ASYA" className="w-full h-full object-contain" />
              </div>
              <a href={String(t('solutions.asya_erp.link'))} target="_blank" rel="noopener noreferrer" className="text-2xl font-bold mb-4 hover:text-primary-container transition-colors">
                {t('solutions.asya_erp.title')}
              </a>
              <p className="text-on-surface-variant mb-6 max-w-lg leading-relaxed">
                {t('solutions.asya_erp.description')}
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {ASYA_TAGS.map((tag) => (
                <span key={tag} className="bg-white px-3 py-1 rounded-full border border-outline-variant text-[10px] font-bold tracking-wider">
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            id="ai-development"
            whileHover={{ scale: 1.02 }}
            className="md:col-span-4 bg-on-tertiary-container text-white p-8 rounded-xl scroll-mt-24"
          >
            <div className="w-12 h-12 bg-tertiary-container rounded flex items-center justify-center mb-6">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-6">{t('solutions.ai_development.title')}</h3>
            <ul className="space-y-4">
              {AI_ITEMS.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-tertiary-container" />
                  <span className="opacity-90">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <div id="infrastructure" className="md:col-span-12 lg:flex gap-6 bg-surface-container border border-outline-variant p-8 rounded-xl scroll-mt-24">
            <div className="lg:w-1/2">
              <div className="w-12 h-12 bg-on-surface rounded flex items-center justify-center text-white mb-6">
                <Network className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold mb-4">{t('solutions.infrastructure.title')}</h3>
              <p className="text-on-surface-variant mb-6 leading-relaxed">
                {t('solutions.infrastructure.description')}
              </p>
            </div>
            <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {INFRA_ITEMS.map((item, idx) => (
                <div key={idx} className="bg-white p-4 rounded border border-outline-variant flex items-center gap-3 hover:border-primary-container transition-colors">
                  {idx === 0 && <Cloud className="text-primary-container" />}
                  {idx === 1 && <Activity className="text-primary-container" />}
                  {idx === 2 && <Zap className="text-primary-container" />}
                  {idx === 3 && <Shield className="text-primary-container" />}
                  <span className="font-bold text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}